import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * Cinematic scroll-zoom hook.
 * Returns a ref and a style object that scales the element
 * based on its position in the viewport.
 *
 * When the element is at the center of the viewport → scale = maxScale (1.0)
 * When it first enters or is about to leave → scale = minScale (0.85)
 * Also applies a subtle opacity fade.
 */
export function useScrollZoom({ minScale = 0.88, maxScale = 1, disabled = false } = {}) {
  const ref = useRef(null);
  const [style, setStyle] = useState({ transform: `scale(${minScale})`, opacity: 0.6 });
  const rafRef = useRef(null);

  const update = useCallback(() => {
    if (!ref.current || disabled) return;

    const rect = ref.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    // Element center position relative to viewport center
    const elementCenter = rect.top + rect.height / 2;
    const viewportCenter = windowHeight / 2;

    // Progress: 0 = element at viewport center, 1 = element at edge
    const distance = Math.abs(elementCenter - viewportCenter);
    const maxDistance = windowHeight * 0.6; // zone of effect
    const progress = Math.min(distance / maxDistance, 1);

    // Ease out cubic for smoother feel
    const eased = 1 - progress * progress;

    const scale = minScale + (maxScale - minScale) * eased;
    const opacity = 0.4 + 0.6 * eased;

    setStyle({
      transform: `scale(${scale.toFixed(4)})`,
      opacity: opacity.toFixed(3),
      transition: 'none',
      willChange: 'transform, opacity',
    });
  }, [minScale, maxScale, disabled]);

  useEffect(() => {
    if (disabled) {
      setStyle({ transform: 'scale(1)', opacity: 1 });
      return;
    }

    const handleScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(update);
    };

    // Initial update
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [update, disabled]);

  return { ref, style };
}
